import { useState } from "react";
import axios from "axios";
import { Mail, Send, Instagram, Twitter, Youtube, Music2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AnimatedSection from "@/components/AnimatedSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialLinks = [
  { icon: Instagram, href: "#", label: "Instagram", handle: "@luxifer6" },
  { icon: Twitter, href: "#", label: "Twitter / X", handle: "@luxifer6" },
  { icon: Youtube, href: "#", label: "YouTube", handle: "Luxifer 6" },
  { icon: Music2, href: "#", label: "Spotify", handle: "Luxifer 6" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully", {
        description: "We'll get back to you soon.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-16">
      {/* Header */}
      <div className="relative py-24 sm:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
            Reach Out
          </p>
          <h1
            data-testid="contact-page-title"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl"
          >
            CONTACT
          </h1>
          <p className="text-white/50 mt-4 max-w-lg">
            For bookings, features, collaborations, and business inquiries.
          </p>
        </div>
      </div>

      <div className="px-6 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <AnimatedSection testId="contact-form-section">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm uppercase tracking-wider text-white/40 mb-2 block">
                  Name
                </label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Your name"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/20 transition-colors duration-300"
                />
              </div>
              <div>
                <label className="text-sm uppercase tracking-wider text-white/40 mb-2 block">
                  Email
                </label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="your@email.com"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/20 transition-colors duration-300"
                />
              </div>
              <div>
                <label className="text-sm uppercase tracking-wider text-white/40 mb-2 block">
                  Message
                </label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  placeholder="Tell us about your project or inquiry..."
                  rows={5}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/20 transition-colors duration-300 resize-none"
                />
              </div>
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="bg-[#D4AF37] text-black hover:bg-white font-semibold px-8 py-3 uppercase tracking-wider text-sm h-auto disabled:opacity-50"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </AnimatedSection>

          {/* Contact Info */}
          <AnimatedSection testId="contact-info-section" delay={200}>
            <div className="space-y-12">
              {/* Email */}
              <div>
                <h3 className="font-heading text-2xl mb-4">BUSINESS EMAIL</h3>
                <a
                  href="mailto:contact@luxifer6.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 text-[#D4AF37] hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-300">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span className="text-sm">contact@luxifer6.com</span>
                </a>
              </div>

              {/* Social */}
              <div>
                <h3 className="font-heading text-2xl mb-6">SOCIAL MEDIA</h3>
                <div className="space-y-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${social.label.toLowerCase().replace(/[\s/]/g, "-")}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:text-[#D4AF37] transition-all duration-300 text-white/40">
                        <social.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60 group-hover:text-white transition-colors">
                          {social.label}
                        </p>
                        <p className="text-xs text-white/25">{social.handle}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Label */}
              <div className="border-t border-white/5 pt-8">
                <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-2">
                  Management
                </p>
                <p className="text-white/40 text-sm">
                  3rd Degree Entertainment
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
}
